import React, { useState } from "react";
import Image from "next/image";
import styles from "../../styles/shap_of_you.module.scss";
import ProductPageDefault from "../../assets/images/ProductPage_Default_Selection.png";
import shapeshare from "../../assets/images/loshare.png";
import { ShareViewScreenProps } from "./types";
import { toast } from "react-toastify";

const ShareViewScreen: React.FC<ShareViewScreenProps> = ({
  navigateToStep,
  virtualTryOnImage,
  capturedImage,
  logStep,
}) => {
  // Debug: Log what we receive
  console.log(
    "ShareViewScreen - virtualTryOnImage:",
    virtualTryOnImage ? "exists" : "null"
  );
  console.log(
    "ShareViewScreen - capturedImage:",
    capturedImage ? "exists" : "null"
  );

  const [hasShared, setHasShared] = useState(false);

  // Determine which image to display (same priority as ProductViewScreen)
  // Priority: virtualTryOnImage (with jewelry) > capturedImage (original) > default
  const displayImage = virtualTryOnImage || capturedImage || ProductPageDefault;
  const isBase64 =
    typeof displayImage === "string" && displayImage.startsWith("data:");

  // Composite the user photo with both logos using Canvas
  const compositeImageWithLogos = (imageUrl: string): Promise<string> => {
    return new Promise((resolve, reject) => {
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");
      if (!ctx) return reject(new Error("Canvas not supported"));

      const mainImg = new window.Image();
      mainImg.crossOrigin = "anonymous";
      mainImg.onload = () => {
        canvas.width = mainImg.width;
        canvas.height = mainImg.height;
        ctx.drawImage(mainImg, 0, 0);

        const padding = canvas.width * 0.03;
        const logoHeight = canvas.height * 0.08;

        const shapeImg = new window.Image();
        shapeImg.crossOrigin = "anonymous";
        shapeImg.onload = () => {
          // Draw Shape of You logo — top-right
          const shapeW = (shapeImg.width / shapeImg.height) * logoHeight;
          ctx.drawImage(shapeImg, canvas.width - padding - shapeW, padding, shapeW, logoHeight);

          // Draw text — bottom center
          const fontSize = Math.max(12, canvas.height * 0.022);
          ctx.font = `${fontSize}px sans-serif`;
          ctx.fillStyle = "#ffffff";
          ctx.textAlign = "center";
          ctx.shadowColor = "rgba(0,0,0,0.6)";
          ctx.shadowBlur = 4;
          const lineSpacing = fontSize * 1.4;
          const textY = canvas.height - padding - lineSpacing;
          ctx.fillText("Shop jewellery made for your face shape", canvas.width / 2, textY);
          ctx.fillText("www.sencogoldanddiamonds.com", canvas.width / 2, textY + lineSpacing);
          ctx.shadowBlur = 0;
          resolve(canvas.toDataURL("image/jpeg", 0.92));
        };
        shapeImg.onerror = () => resolve(canvas.toDataURL("image/jpeg", 0.92));
        shapeImg.src = (shapeshare as any).src ?? (shapeshare as any);
      };
      mainImg.onerror = reject;
      mainImg.src = imageUrl;
    });
  };

  // Handle download functionality
  const handleDownload = async () => {
    try {
      const downloadImage = virtualTryOnImage || capturedImage;
      if (!downloadImage) {
        toast.error("No image available to download");
        return;
      }
      const composited = await compositeImageWithLogos(downloadImage);
      const link = document.createElement("a");
      link.href = composited;
      link.download = "senco-jewelry-tryon.jpg";
      link.style.display = "none";
      document.body.appendChild(link);
      link.click();
      setTimeout(() => document.body.removeChild(link), 100);
      if (logStep) logStep("share_view_download_clicked");
      toast.success("Image downloaded successfully");
    } catch (error: any) {
      console.error("Error downloading:", error);
      toast.error("Failed to download image. Please try again.");
    }
  };

  // Handle share functionality
  const handleShare = async () => {
    try {
      if (!isBase64) {
        toast.error("No image available to share");
        return;
      }

      const composited = await compositeImageWithLogos(displayImage as string);

      const base64Response = await fetch(composited);
      const blob = await base64Response.blob();
      const file = new File([blob], "senco-jewelry-tryon.jpg", {
        type: "image/jpeg",
      });

      if (
        "share" in navigator &&
        "canShare" in navigator &&
        (navigator as any).canShare({ files: [file] })
      ) {
        await navigator.share({
          title: "My Senco Jewellery Look",
          text: "Check out how I look with Senco Jewellery! They suggested pieces based on my face shape —https://sencogoldanddiamonds.com/shape-of-you you should try this too!",
          files: [file],
        });
        setHasShared(true);
      } else if ("share" in navigator) {
        await navigator.share({
          title: "My Senco Jewellery Look",
          text: "Check out how I look with Senco Jewellery! They suggested pieces based on my face shape —https://sencogoldanddiamonds.com/shape-of-you you should try this too!",
          url: window.location.href,
        });
        setHasShared(true);
      } else {
        const link = document.createElement("a");
        link.href = composited;
        link.download = "senco-jewelry-tryon.jpg";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        toast.success("Image downloaded successfully");
      }
    } catch (error: any) {
      if (error.name !== "AbortError") {
        console.error("Error sharing:", error);
        toast.error("Failed to share. Please try again.");
      }
    }
  };

  return (
    <div className="d-md-none w-100">
      <div className={styles.card_share}>
        <div className={styles.card}>
          <div className={styles.image_sh}>
            {isBase64 ? (
              <Image
                src={displayImage}
                alt="Jewelry Model"
                className={styles.image}
                width={500}
                height={500}
                unoptimized
              />
            ) : (
              <Image
                src={displayImage}
                alt="Jewelry Model"
                className={styles.image}
              />
            )}
          </div>
          {/* Top Left Logo Text */}
          <div className={styles.topLeft}>
            <button
              className={styles.backshare}
              onClick={() => {
                if (logStep) {
                  logStep("share_view_back_clicked");
                }
                navigateToStep("productView");
              }}
            >
              <i className="bi bi-arrow-left"></i>
            </button>
            <Image
              src={shapeshare}
              alt="Jewelry Model"
              className={styles.image}
            />
          </div>
          <div className={styles.bottom_sharre}>
            <button
              className={styles.dwn}
              onClick={handleDownload}
              style={{ cursor: "pointer" }}
            >
              <i className="bi bi-download"></i>
            </button>
            {!hasShared && (
              <p>
                Shop jewellery made for your face shape
                <a href="#">www.sencogoldanddiamonds.com </a>
              </p>
            )}
          </div>
          {/* Bottom Share Button */}
          <div className={styles.shareWrapper}>
            <button
              className={styles.retake}
              onClick={() => {
                if (logStep) {
                  logStep("share_view_retake_clicked");
                }
                navigateToStep("uploadOrScan");
              }}
            >
              <span className={styles.icon}>
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M5.46257 4.43262C7.21556 2.91688 9.5007 2 12 2C17.5228 2 22 6.47715 22 12C22 14.1361 21.3302 16.1158 20.1892 17.7406L17 12H20C20 7.58172 16.4183 4 12 4C9.84982 4 7.89777 4.84827 6.46023 6.22842L5.46257 4.43262ZM18.5374 19.5674C16.7844 21.0831 14.4993 22 12 22C6.47715 22 2 17.5228 2 12C2 9.86386 2.66979 7.88416 3.8108 6.25944L7 12H4C4 16.4183 7.58172 20 12 20C14.1502 20 16.1022 19.1517 17.5398 17.7716L18.5374 19.5674Z"
                    fill="white"
                  />
                </svg>
              </span>
              Retake
            </button>
            <button className={styles.shareBtn} onClick={handleShare}>
              <span className={styles.icon}>
                <svg
                  width="20"
                  height="18"
                  viewBox="0 0 20 18"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M11.0002 1V5C4.42522 6.028 1.98022 11.788 1.00022 17C0.963219 17.206 6.38422 11.038 11.0002 11V15L19.0002 8L11.0002 1Z"
                    stroke="white"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </span>
              Share
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ShareViewScreen;
